import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BedDouble, CarFront, Compass, Plane, Clock, Headset, Handshake, Building2, Ship } from "lucide-react";
import { api, Listing, money, mediaUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { PageHeader, Spinner, Eyebrow } from "@/components/site/Bits";
import { InquiryForm } from "@/components/site/InquiryForm";

const statusLabel = (v?: string) => (v === "live" ? "Available now" : v === "inquiry" ? "Inquiry only" : "Coming soon");

export const Services = () => {
  const { s } = useSettings();
  const st = s?.service_status || {};
  const items: { icon: any; t: string; d: string; to: string; k: string; fixed?: string }[] = [
    { icon: BedDouble, t: "Accommodation in Accra", d: "Private stays in Accra, Ghana. Request dates, receive a reference, and our team confirms.", to: "/stays", k: "stays" },
    { icon: CarFront, t: "Vehicle rentals", d: "Bookable online in Accra, Ghana. U.S. locations in Houston and El Paso, Texas are being developed — rates on request.", to: "/vehicles", k: "vehicles" },
    { icon: Compass, t: "Tours & Experiences", d: "Curated experiences arranged on inquiry. Itinerary and price confirmed in writing.", to: "/tours", k: "tours" },
    { icon: Plane, t: "Airport Pickups", d: "Available now at Accra International Airport, Ghana. Houston and Liberia coming soon.", to: "/airport-transfers", k: "transfers" },
    { icon: Headset, t: "Airbnb Guest Support", d: "Hands-on support for guests in Accra and the U.S. — arrival help, questions and assistance during your stay.", to: "/support", k: "guest_support" },
    { icon: Handshake, t: "Investors & Partnerships", d: "Introductory conversations with investors and business partners. Not an offer of any security or investment.", to: "/investors", k: "investors", fixed: "Inquiries open" },
    { icon: Building2, t: "Gant Estates", d: "A proposed residential concept planned for 2027. Not approved, funded or under construction.", to: "/estates", k: "estates", fixed: "In development" },
    { icon: Ship, t: "Cargo Shipping (U.S. to Liberia)", d: "Planned for 2027. Not operating yet — register interest for launch updates.", to: "/shipping", k: "shipping", fixed: "In development" },
  ];
  return (
    <div>
      <PageHeader eyebrow="What we do" title="Services" intro="We publish only what we can deliver today. Everything else lives on our Coming Soon page until it is ready." />
      <div className="container-x grid gap-6 py-14 md:grid-cols-2">
        {items.map((it) => (
          <Link key={it.k} to={it.to} className="card-lux group flex flex-col p-8 transition-transform duration-300 hover:-translate-y-1" data-testid={`service-card-${it.k}`}>
            <div className="flex items-center justify-between"><it.icon className="text-gold" /><span className="font-mono text-[10px] uppercase tracking-widest text-navy/50" data-testid={`service-status-${it.k}`}>{it.fixed || statusLabel(st[it.k])}</span></div>
            <h2 className="mt-8 text-2xl sm:text-3xl">{it.t}</h2>
            <p className="mt-3 text-sm text-navy/60 md:text-base">{it.d}</p>
            <span className="mt-6 flex items-center gap-1.5 text-sm font-semibold">Explore <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></span>
          </Link>
        ))}
      </div>
    </div>
  );
};

export const Tours = () => {
  const [tours, setTours] = useState<Listing[] | null>(null);
  const [pick, setPick] = useState<Listing | null>(null);
  useEffect(() => {
    api.get("/listings/tour").then((r) => setTours(r.data)).catch(() => setTours([]));
  }, []);
  return (
    <div>
      <PageHeader eyebrow="Inquiry only" title="Tours & Experiences" intro="Tell us what you would love to experience. Availability, itinerary and pricing are confirmed by our team before anything is booked." />
      <div className="container-x grid gap-12 py-14 lg:grid-cols-[1fr_440px]">
        <div>
          {!tours ? <Spinner /> : tours.length === 0 ? (
            <div className="overflow-hidden rounded-3xl"><img src="/images/tours.jpg" alt="Accra" className="h-[360px] w-full object-cover" /></div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2" data-testid="tours-grid">
              {tours.map((t) => (
                <button key={t.id} onClick={() => setPick(t)} className={`card-lux overflow-hidden text-left transition-shadow ${pick?.id === t.id ? "ring-2 ring-gold" : ""}`} data-testid={`tour-card-${t.id}`}>
                  {t.images?.[0] && <img src={mediaUrl(t.images[0])} alt={t.title} className="aspect-[4/3] w-full object-cover" />}
                  <div className="p-5">
                    <h3 className="font-sans text-base font-semibold">{t.title}</h3>
                    <p className="mt-1 flex items-center gap-1.5 text-xs text-navy/55"><Clock size={12} /> {t.duration || "Flexible"}{t.city ? ` · ${t.city}` : ""}</p>
                    <p className="mt-2 line-clamp-3 text-sm text-navy/65">{t.description}</p>
                    {t.price_from ? <p className="mt-3 font-mono text-sm font-bold">From {money(t.price_from, t.currency)}</p> : <p className="mt-3 text-xs text-navy/50">Price on inquiry</p>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="lg:sticky lg:top-24 lg:self-start">
          <Eyebrow>{pick ? `Inquiring about: ${pick.title}` : "General tour inquiry"}</Eyebrow>
          <div className="mt-4"><InquiryForm key={pick?.id || "general"} kind="tour" extras={["date", "party"]} subjectId={pick?.id} subjectTitle={pick?.title} testid="tour-inquiry" /></div>
        </div>
      </div>
    </div>
  );
};

export const Transfers = () => {
  const { s } = useSettings();
  return (
    <div>
      <PageHeader eyebrow={s?.service_status?.transfers === "live" ? "Available now in Accra · Houston & Liberia coming soon" : "Inquiry only"} title="Airport Pickups" intro={s?.transfers?.intro} testid="transfers-title" />
      <div className="container-x grid gap-12 py-14 lg:grid-cols-[1fr_480px]">
        <div className="space-y-6">
          <figure className="relative overflow-hidden rounded-3xl" data-testid="transfer-hero-image"><img src="/images/airport-acc-aerial.jpg" alt="Accra International Airport, Ghana" className="h-[340px] w-full object-cover" />
            <figcaption className="absolute bottom-3 left-3 rounded-full bg-black/55 px-3 py-1 text-xs text-white backdrop-blur">Accra International Airport (ACC) · Available now</figcaption></figure>
          <div className="grid gap-3 sm:grid-cols-3" data-testid="transfer-airport-gallery">
            {[["airport-acc-night.jpg", "Accra terminal forecourt at night (photo shows the former Kotoka name)", "Accra · Pick-up forecourt"], ["airport-acc-arrivals.jpg", "Accra International Airport arrivals hall exit", "Accra · Arrivals meeting point"], ["airport-roberts-liberia.jpg", "Roberts International Airport, Liberia", "Liberia · Coming soon"]].map(([src, alt, cap]) => (
              <figure key={src} className="relative overflow-hidden rounded-2xl"><img src={`/images/${src}`} alt={alt} loading="lazy" className="h-40 w-full object-cover" />
                <figcaption className="absolute bottom-2 left-2 rounded-full bg-black/55 px-2.5 py-0.5 text-[11px] text-white backdrop-blur">{cap}</figcaption></figure>
            ))}
          </div>
          <div className="card-lux p-6">
            <p className="label">Airports we can arrange on request</p>
            <ul className="mt-3 space-y-2 text-sm" data-testid="transfer-airports">{s?.transfers?.airports?.map((a) => <li key={a} className="flex items-center gap-2"><Plane size={14} className="text-gold" /> {a}</li>)}</ul>
          </div>
        </div>
        <InquiryForm kind="transfer" extras={["date", "flight", "party", "pickup", "dropoff"]} cta="Request an airport pickup" testid="transfer-inquiry" />
      </div>
    </div>
  );
};
